import { useCallback, useEffect, useState } from 'react';

import { FilterMatchMode } from 'primereact/api';
import type {
  DataTableFilterMetaData,
  DataTableSortMeta
} from 'primereact/datatable';
import type { BaseFilter, selectedDate, Task } from 'types/interfaces';

import { TaskService } from 'components/services/TaskService';
export function useDistributionTasks({
  visible,
  hasDeptSelection,
  deptValueForApi,
  dateFilter,
  multiSortMeta,
  filters,
  travelTimeOptimization
}: {
  visible: boolean;
  hasDeptSelection: boolean;
  deptValueForApi: string;
  dateFilter: selectedDate;
  multiSortMeta: DataTableSortMeta[] | undefined | null;
  filters: Record<string, DataTableFilterMetaData>;
  travelTimeOptimization: boolean;
}) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  const loadTasks = useCallback(async () => {
    if (!visible) return;
    if (!hasDeptSelection) {
      setTasks([]);
      return;
    }
    const sort = multiSortMeta?.find((x) => x !== undefined);

    const columnFilters: BaseFilter[] = Object.entries(filters)
      .filter(
        ([, f]) => f.value !== null && f.value !== undefined && f.value !== ''
      )
      .map(([field, f]) => ({
        field,
        value: String(f.value),
        operator: f.matchMode ?? FilterMatchMode.CONTAINS
      }));

    const travelTimeFilters: BaseFilter[] = [];
    if (travelTimeOptimization) {
      travelTimeFilters.push({
        field: 'requiredEmployees',
        value: '1',
        operator: FilterMatchMode.EQUALS
      });
      travelTimeFilters.push({
        field: 'hasCoordinates',
        value: 'true',
        operator: FilterMatchMode.CONTAINS
      });
    }

    const dateFrom = dateFilter.dateFrom
      ? new Date(parseInt(dateFilter.dateFrom)).toISOString()
      : '';
    const dateUntil = dateFilter.dateUntil
      ? new Date(parseInt(dateFilter.dateUntil)).toISOString()
      : '';
    const dateFilters: BaseFilter[] = [];
    if (travelTimeOptimization) {
      dateFilters.push(
        {
          field: 'plannedStartDateBetween',
          value: dateFrom,
          additionalValue: dateUntil,
          operator: FilterMatchMode.BETWEEN
        },
        {
          field: 'plannedEndDateBetween',
          value: dateFrom,
          additionalValue: dateUntil,
          operator: FilterMatchMode.BETWEEN
        }
      );
    } else {
      dateFilters.push({
        field: 'creationDateBetween',
        value: dateFrom,
        additionalValue: dateUntil,
        operator: FilterMatchMode.BETWEEN
      });
    }

    const filtersPayload = JSON.stringify([
      ...columnFilters,
      ...travelTimeFilters,
      {
        field: 'department',
        value: deptValueForApi,
        operator: FilterMatchMode.IN
      },
      ...dateFilters
    ]);

    try {
      setTasksLoading(true);
      const resp = await TaskService.getPersonAssignment({
        sortBy: sort?.field ?? 'creationDate',
        sortDirection: sort && sort.order === 1 ? 'asc' : 'desc',
        filters: filtersPayload
      });
      const value = resp.data?.value;
      const list: Task[] = Array.isArray(value)
        ? (value as unknown as Task[])
        : Array.isArray((value as any)?.itemsList)
        ? ((value as any).itemsList as Task[])
        : [];
      setTasks(list);
    } catch (e) {
      console.error('Failed to load tasks for employee distribution:', e);
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, [
    visible,
    hasDeptSelection,
    deptValueForApi,
    dateFilter.dateFrom,
    dateFilter.dateUntil,
    multiSortMeta,
    filters,
    travelTimeOptimization
  ]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  return { tasks, tasksLoading, reloadTasks: loadTasks };
}
